import Vue from 'vue';
import Vuex from 'vuex';
import router from '../router';
import axios from "axios";
import tokenManagerService from "../services/tokenManager";

const backendUrl = process.env.VUE_APP_BACKEND_URL || "";

Vue.use(Vuex)

const requestWithToken = async (callback) => {
  try {
    const token = await tokenManagerService.getValidToken();
    if (token) {
      return await callback(token);
    }
  } catch (error) {
    console.log("error getting token", error);
  }
  return null;
};

export default new Vuex.Store({
  state: {
    user: null,
    userGroups: [],
    userPermissions: [],
    dropdowns: {},
    appTableNames: [],
    platforms: [],
    apps: {
      "com.codeway.cleanerplus": "Cleaner+",
      "com.codeway.scannerplus": "Scanner+",
      "com.codeway.Mindfulness": "Tell me",
      "com.codeway.routease": "Routease",
      "com.codeway.rockidentifier": "Rock identifier"
    },
    v2Apps: {
      "com.codeway.pixelup": "Pixelup"
    },
    allApps: {
      "com.storage.androidcleaner": "Android Cleaner",
      "com.codeway.chatappweb": "Ask Ai Web",
      "com.codeway.chatapp": "Ask Ai",
      "com.codeway.wonder": "Wonder",
      "com.codeway.cleanerplus": "Cleaner+",
      "com.codeway.scannerplus": "Scanner+",
      "com.codeway.pixelup": "Pixelup",
      "com.codewaystudios.facedance": "Facedance",
      "com.codewaystudios.selfbooth": "Voi",
      "com.codeway.couplesapp": "Couples",
      "com.codeway.rockidentifier": "Rock identifier",
      "com.codeway.homework": "Nerd AI",
      "com.codeway.AIKeyboard": "Type AI",
      "com.codeway.AIFriend": "Genesia",
      "com.codespaceapps.you": "You",
      "com.codeway.plantapp": "Plantify",
      "com.codeway.brainapp": "IQ Masters",
      "com.codespaceapps.aichat": "AI Chat",
      "com.codespaceapps.cleanerv2": "Cleaner V2",
      "com.codespaceapps.aiartapp": "Dali",
      "com.visionwizard.holybible": "Bible Path",
      "com.deepvision.shortdrama": "Drama Pops",
      "com.codespaceapps.routineplanner": "Mindway",
      "com.codeway.aitutor": "Learna AI",
      "com.visionwizard.chatbot": "AI Search And Chat",
      "com.visionwizard.sleeptrack": "Sleepway",
      "com.codespaceapps.listeningapp": "Listen AI",
      "com.codespaceapps.aibook": "Sparkle",
      "com.deepvision.interiordesign": "Interio AI",
      "com.holypic.holypicchatweb": "Holypic Chat Web",
      "com.deepvision.datinghelper": "AI Wingman - Rizzgod",
      "com.deepvision.screenlimiter": "LIMIT: Screen Time Control",
      "com.visionwizard.workout": "FitEase",
      "com.visionwizard.fastingapp": "Fastie: Intermittent Fasting",
      "com.codeway.videoapp": "Video App",
      "com.visionwizard.pulse": "Heart In",
      "com.codespaceapps.meetingtranscriber": "Smart Noter AI",
      "com.visionwizard.muscle": "Muscle Up",
      "com.visionwizard.eatwise": "Eatwise AI",
      "com.deepvision.aitattoo": "Tattoo AI",
      "com.deepvision.ainovelapp": "AI Novel Arc",
      "com.codewaygames.wordgame": "Word Voyage",
      "com.codeway.magicretake": "Magic Retake",
      "com.codespaceapps.captionsapp": "AI Edit & Captions",
      "com.codeway.Mindfulness": "Tell me",
      "com.codeway.glpone": "Dosely",
    }
  },
  mutations: {
    SET_USER(state, payload) {
      state.user = payload;
    },
    SET_USER_GROUPS(state, groups) {
      state.userGroups = groups || [];
    },
    SET_USER_PERMISSIONS(state, permissions) {
      state.userPermissions = permissions || [];
    },
    SET_DROPDOWNS(state, payload){
      console.log("setting dropdowns: ", payload);
      state.dropdowns = payload;
    },
    SET_APP_TABLE_NAMES(state, payload){
      state.appTableNames = payload;
    },
    SET_PLATFORMS(state, payload){
      state.platforms = payload;
    }
  },
  actions: {
    async setUser({ commit, dispatch }) {
      try {
        // Get user info from backend (includes permissions from database)
        const result = await requestWithToken(async (token) => {
          const response = await axios.get(backendUrl + "/auth/user-info", {
            headers: { authorization: `Bearer ${token}` }
          });
          return response.data;
        });

        if (result && result.user) {
          commit("SET_USER", {
            displayName: result.user.name,
            email: result.user.email
          });
          commit("SET_USER_GROUPS", result.user.groups || []);
          commit("SET_USER_PERMISSIONS", result.user.permissions || []);
          dispatch("setDropdowns");
        } else {
          // Fallback to token-based info if backend call fails
          const userInfo = await tokenManagerService.getUserInfo();
          const groups = await tokenManagerService.getUserGroups();
          
          if (userInfo) {
            commit("SET_USER", {
              displayName: userInfo.name,
              email: userInfo.email
            });
            commit("SET_USER_GROUPS", groups);
            commit("SET_USER_PERMISSIONS", []);
            dispatch("setDropdowns");
          } else {
            commit("SET_USER", null);
            commit("SET_USER_GROUPS", []);
            commit("SET_USER_PERMISSIONS", []);
          }
        }
      } catch (error) {
        console.error("Error setting user:", error);
        commit("SET_USER", null);
        commit("SET_USER_GROUPS", []);
        commit("SET_USER_PERMISSIONS", []);
      }
    },
    
    clearUser({ commit }) {
      commit("SET_USER", null);
      commit("SET_USER_GROUPS", []);
      commit("SET_USER_PERMISSIONS", []);
    },

    async setDropdowns({ commit }) {
      await requestWithToken(async (token) => {
        console.log("requesting to: ", backendUrl + "/get-dropdown-options");
        
        try {
          const response = await axios.get(backendUrl + "/get-dropdown-options", {
            headers: { authorization: `Bearer ${token}` }
          });
          console.log("response", response);
          commit("SET_DROPDOWNS", response.data);
        } catch (error) {
          console.log("error", error);
        }

        try {
          const response = await axios.get(backendUrl + "/get-app-table-names", {
            headers: { authorization: `Bearer ${token}` }
          });
          commit("SET_APP_TABLE_NAMES", response.data);
        } catch (error) {
          console.log("error fetching app table names", error);
        }

        try {
          const response = await axios.get(backendUrl + "/get-platforms", {
            headers: { authorization: `Bearer ${token}` }
          });
          commit("SET_PLATFORMS", response.data);
        } catch (error) {
          console.log("error fetching platforms", error);
        }
      });
    }
  },
  getters: {
    user(state){
      return state.user
    },
    userGroups(state) {
      return state.userGroups;
    },
    userPermissions(state) {
      return state.userPermissions;
    },
    // Check if user has a specific permission (from database)
    hasPermission: (state) => (permissionId) => {
      return state.userPermissions.includes(permissionId);
    },
    // Coupon access is now based on database permission
    hasCouponAccess(state) {
      return state.userPermissions.includes('coupon-upload');
    },
    apps(state) {
      return Object.fromEntries(Object.entries(state.apps).sort((a, b) => a[1].localeCompare(b[1])));
    },
    campaigns(state) {
      return state.campaigns
    },
    v2Apps(state){
      return Object.fromEntries(Object.entries(state.v2Apps).sort((a, b) => a[1].localeCompare(b[1])));
    },
    allApps(state){
      const sortedObject = Object.fromEntries(Object.entries(state.allApps).sort((a, b) => a[1].localeCompare(b[1])));
      return sortedObject
    },
    influencerAdgroups(state){
      if(state.dropdowns.influencer_adgroups){
      return state.dropdowns.influencer_adgroups.sort()
      }
    },
    influencerCampaigns(state){
      if(state.dropdowns.influencer_campaigns){
        return state.dropdowns.influencer_campaigns.sort()
      }        
    },
    appTableNames(state){
      return [...state.appTableNames].sort((a, b) => a.localeCompare(b));
    },
    platforms(state){
      return [...state.platforms].sort((a, b) => a.localeCompare(b));
    }
  }
})
